/**
 * A named text style, as a Tiptap mark.
 *
 * One mark type parameterised by a `styleId` attribute, rather than one mark
 * per style: the set of styles is document data, not something known when
 * the editor is built, so there is no fixed list of mark types to register.
 *
 * Renders as a `<span class="text-style--<id>">`, never a `style` attribute —
 * `domain/sanitise.ts` strips inline `style` on purpose, and this has to
 * survive that same sanitising every other mark does.
 */
import { Mark, mergeAttributes } from '@tiptap/core'

import { textStyleClassName, textStyleIdFromClass } from '@/domain/layout/textStyles'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    reportTextStyle: {
      setReportTextStyle: (styleId: string) => ReturnType
      unsetReportTextStyle: () => ReturnType
    }
  }
}

export const TextStyleMark = Mark.create({
  name: 'reportTextStyle',

  addAttributes() {
    return {
      styleId: { default: null },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span',
        // Not consuming: the same span may carry a colour or a size for the other marks.
        consuming: false,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false
          for (const className of element.classList) {
            const id = textStyleIdFromClass(className)
            if (id) return { styleId: id }
          }
          return false
        },
      },
    ]
  },

  renderHTML({ mark, HTMLAttributes }) {
    const styleId = mark.attrs['styleId'] as string | null
    if (!styleId) return ['span', mergeAttributes(HTMLAttributes), 0]
    return ['span', mergeAttributes(HTMLAttributes, { class: textStyleClassName(styleId) }), 0]
  },

  addCommands() {
    return {
      setReportTextStyle:
        (styleId: string) =>
        ({ commands }) =>
          commands.setMark(this.name, { styleId }),
      unsetReportTextStyle:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    }
  },
})
